'use client';

import React, { useState, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Loader2, X, Check, Gift, Tag, AlertCircle } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { couponCodeSchema, type CouponCodeFormData } from '@/lib/validations/discount';
import { useDiscounts } from '@/hooks/useDiscounts';
import { formatDiscountDisplay } from '@/lib/utils/discount';
import { CartItemPopulated } from '@/lib/Apis/models';

interface CouponCodeInputProps {
  onApplyCoupon?: (code: string) => void;
  onRemoveCoupon?: () => void;
  userId?: string;
  cartItems?: CartItemPopulated[];
  className?: string;
}

export function CouponCodeInput({
  onApplyCoupon,
  onRemoveCoupon,
  userId,
  cartItems,
  className = '',
}: CouponCodeInputProps) {
  const {
    appliedCoupon,
    couponError,
    isValidatingCoupon,
    validateAndApplyCoupon,
    removeCoupon,
  } = useDiscounts();

  const [localError, setLocalError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CouponCodeFormData>({
    resolver: zodResolver(couponCodeSchema),
  });

  const couponCodeValue = watch('code');
  const errorMessage =
    errors.code?.message || localError || couponError || null;

  const triggerShake = () => {
    if (!inputRef.current) return;
    inputRef.current.classList.add('animate-shake');
    setTimeout(() => {
      inputRef.current?.classList.remove('animate-shake');
    }, 400);
  };

  const onSubmit = useCallback(
    async (data: CouponCodeFormData) => {
      setLocalError(null);
      const result = await validateAndApplyCoupon(data.code, userId, cartItems);

      if (!result.success) {
        setLocalError(result.error || 'Invalid coupon code');
        triggerShake();
        return;
      }

      reset();
      onApplyCoupon?.(data.code);
      inputRef.current?.blur();
    },
    [onApplyCoupon, reset, userId, validateAndApplyCoupon]
  );

  const handleRemove = () => {
    removeCoupon();
    onRemoveCoupon?.();
    reset();
    setLocalError(null);
  };


  if (appliedCoupon) {
    return (
      <div
        className={`relative rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50 p-4 shadow-sm ${className}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
              <Check className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-900">
                Coupon Applied
              </p>
              <p className="text-sm font-medium text-emerald-700">
                {appliedCoupon.code} • {formatDiscountDisplay(appliedCoupon)}
              </p>
            </div>
          </div>

          <button
            onClick={handleRemove}
            aria-label="Remove coupon"
            className="group flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-200 bg-white transition hover:border-red-300 hover:bg-red-50"
          >
            <X className="h-4 w-4 text-emerald-600 group-hover:text-red-600" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center gap-2">
        <Gift className="h-5 w-5 text-slate-500" />
        <label
          htmlFor="coupon-code"
          className="text-sm font-semibold text-slate-700"
        >
          Have a coupon code?
        </label>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
            <Tag
              className={`h-5 w-5 transition-colors ${isFocused ? 'text-slate-400' : 'text-slate-400'
                }`}
            />
          </div>

          <Input
            id="coupon-code"
            {...register('code', {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const upperCaseValue = e.target.value.toUpperCase();
                setValue('code', upperCaseValue);
              },
            })}
            placeholder="Enter code (e.g. SAVE10)"
            maxLength={50}
            disabled={isValidatingCoupon || isSubmitting}
            aria-invalid={!!errorMessage}
            className={`pl-4 h-11 rounded-lg transition-all shadow-sm ${errorMessage
              ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-500/20'
              : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </div>

        <motion.button
          whileHover={{ scale: 1.02, backgroundColor: '#05c47e' }}
          whileTap={{ scale: 0.98 }}
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={!couponCodeValue || isValidatingCoupon || isSubmitting}
          className={`h-11 px-5 rounded-lg disabled:bg-slate-300 disabled:text-white text-white transition-all duration-200 ${isValidatingCoupon ? 'opacity-50' : ''}`}
          style={{
            backgroundColor: '#04AA6D',
          }}
        >
          {isValidatingCoupon ? (
            <>
              <div className='flex items-center gap-2'>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking
              </div>
            </>
          ) : (
            'Apply'
          )}
        </motion.button>
      </div>
      {/* </div> */}

      {errorMessage && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}
    </div>
  );
}
